"use client";

import { useState, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { validateFields } from "@/lib/utils";
import type { Section, VariantDetails, ListItem } from "../types";
import { DEFAULT_MESSAGES } from "../constants";
import {
  showToast,
  extractItemsFromResponse,
  extractIdFromResponse,
  buildPayload,
  getUniversitySettingsId,
  getDefaultFieldValues,
  mapItemToFormFields,
} from "../utils";

interface UseListActionsProps {
  selectedSection: Section;
  variantDetails?: VariantDetails;
  onSave: (updatedSection: Section) => void;
}

export const useListActions = ({
  selectedSection,
  variantDetails,
  onSave,
}: UseListActionsProps) => {
  const [listItems, setListItems] = useState<ListItem[]>([]);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [componentFields, setComponentFields] = useState<Record<string, any>>({});
  const [componentFieldErrors, setComponentFieldErrors] = useState<Record<string, boolean>>({});
  const [componentFieldMessages, setComponentFieldMessages] = useState<Record<string, string>>({});
  
  const lastFetchedComponentId = useRef<string | number | null>(null);
  const dispatch = useAppDispatch();
  
  const currentUser = useAppSelector((state) => state.user?.user);
  const reduxState = useAppSelector((state) =>
    variantDetails?.selector ? variantDetails.selector(state.university) : null
  );

  const componentFieldsConfig = variantDetails?.componentFields || [];
  const fetchMethod = variantDetails?.fetchMethod;
  const deleteMethod = variantDetails?.deleteMethod;
  const createMethod = variantDetails?.createMethod;
  const updateMethod = variantDetails?.updateMethod;

  // ---------------------------------------------------------------------------
  // Fetch Items
  // ---------------------------------------------------------------------------

  const fetchListItems = useCallback(async () => {
    const componentId = selectedSection?.componentId;
    if (!fetchMethod || !componentId) {
      setListItems([]);
      return;
    }

    try {
      const response = await fetchMethod(Number(componentId));
      setListItems(extractItemsFromResponse(response));
    } catch (error) {
      console.error("Failed to fetch list items:", error);
      setListItems([]);
    }
  }, [selectedSection?.componentId, fetchMethod]);

  // ---------------------------------------------------------------------------
  // Initialize
  // ---------------------------------------------------------------------------

  const initializeData = useCallback(() => {
    const componentId = selectedSection?.componentId;

    // Try to use pre-loaded data
    if (selectedSection?.data && componentId) {
      const items = Array.isArray(selectedSection.data)
        ? selectedSection.data
        : selectedSection.data.items || [];

      if (Array.isArray(items) && items.length > 0) {
        setListItems(items);
        lastFetchedComponentId.current = componentId;
        return true;
      }
    }

    if (!componentId) {
      lastFetchedComponentId.current = null;
      setListItems([]);
      return false;
    }

    if (lastFetchedComponentId.current === componentId) {
      return false;
    }

    if (!fetchMethod) return false;

    lastFetchedComponentId.current = componentId;
    fetchListItems();
    return true;
  }, [selectedSection?.componentId, selectedSection?.data, fetchMethod, fetchListItems]);

  const initializeFormDefaults = useCallback(() => {
    if (Object.keys(componentFields).length === 0 && componentFieldsConfig.length > 0) {
      setComponentFields(getDefaultFieldValues(componentFieldsConfig));
    }
  }, [componentFields, componentFieldsConfig]);

  // ---------------------------------------------------------------------------
  // Form Actions
  // ---------------------------------------------------------------------------

  const handleFieldsUpdate = useCallback(
    (updatedData: any) => {
      setComponentFields(updatedData);
      if (variantDetails?.setAction) {
        dispatch(
          variantDetails.setAction({
            ...reduxState,
            ...updatedData,
            id: reduxState?.id || "",
          })
        );
      }
    },
    [variantDetails, dispatch, reduxState]
  );

  const resetForm = useCallback(() => {
    setComponentFields(getDefaultFieldValues(componentFieldsConfig));
    setEditingItemId(null);
    setComponentFieldErrors({});
    setComponentFieldMessages({});
  }, [componentFieldsConfig]);

  // ---------------------------------------------------------------------------
  // CRUD Operations
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (!variantDetails) {
      showToast.error(DEFAULT_MESSAGES.CONFIG_NOT_FOUND);
      return;
    }

    const { errors, messages } = validateFields(componentFieldsConfig, componentFields);
    setComponentFieldErrors(errors);
    setComponentFieldMessages(messages);

    if (Object.keys(errors).length > 0) {
      showToast.validation(DEFAULT_MESSAGES.VALIDATION_ERROR_TITLE, DEFAULT_MESSAGES.VALIDATION_ERROR_DESCRIPTION);
      return;
    }

    const isUpdate = editingItemId && !String(editingItemId).startsWith("temp_");
    const universitySettingsId = getUniversitySettingsId(selectedSection?.componentId, listItems);

    if (!universitySettingsId) {
      showToast.error(DEFAULT_MESSAGES.SETTINGS_ID_REQUIRED);
      return;
    }

    try {
      const payload = buildPayload(componentFields, universitySettingsId, !!isUpdate, currentUser?.id);

      const operationConfig = {
        create: {
          successMessage: DEFAULT_MESSAGES.SUCCESS_CREATE,
          execute: async () => {
            if (!createMethod) throw new Error(DEFAULT_MESSAGES.CREATE_NOT_CONFIGURED);
            const response = await createMethod(payload);
            const id = extractIdFromResponse(response);
            if (!id) throw new Error(DEFAULT_MESSAGES.RESPONSE_ID_ERROR);
            return id;
          },
        },
        update: {
          successMessage: DEFAULT_MESSAGES.SUCCESS_UPDATE,
          execute: async () => {
            if (!updateMethod) throw new Error(DEFAULT_MESSAGES.UPDATE_NOT_CONFIGURED);
            if (!editingItemId || isNaN(Number(editingItemId))) {
              throw new Error(DEFAULT_MESSAGES.ID_REQUIRED);
            }
            await updateMethod(Number(editingItemId), payload);
            return editingItemId;
          },
        },
      };

      const operation = isUpdate ? operationConfig.update : operationConfig.create;
      await operation.execute();

      showToast.success(operation.successMessage);
      resetForm();
      await fetchListItems();

      // Update parent with new data
      if (universitySettingsId && fetchMethod) {
        try {
          const response = await fetchMethod(universitySettingsId);
          const updatedItems = extractItemsFromResponse(response);
          onSave({
            ...selectedSection,
            componentId: String(universitySettingsId),
            data: updatedItems,
          });
        } catch (error) {
          console.error("Failed to fetch updated data:", error);
        }
      }
    } catch (error: any) {
      console.error("Failed to save:", error);
      showToast.error(error.message || DEFAULT_MESSAGES.ERROR_SAVE);
    }
  }, [
    variantDetails,
    componentFieldsConfig,
    componentFields,
    editingItemId,
    selectedSection,
    listItems,
    currentUser?.id,
    createMethod,
    updateMethod,
    fetchMethod,
    resetForm,
    fetchListItems,
    onSave,
  ]);

  const handleEdit = useCallback(
    (item: ListItem) => {
      setComponentFields(mapItemToFormFields(item, componentFieldsConfig));
      setEditingItemId(String(item.id));
      setComponentFieldErrors({});
      setComponentFieldMessages({});
    },
    [componentFieldsConfig]
  );

  const handleDelete = useCallback(
    async (item: ListItem, confirmMessage: string) => {
      // Note: Confirmation should be handled by caller using DeleteDialog
      if (!deleteMethod) {
        showToast.error(DEFAULT_MESSAGES.DELETE_NOT_CONFIGURED);
        return;
      }

      try {
        await deleteMethod(Number(item.id));
        showToast.success(DEFAULT_MESSAGES.SUCCESS_DELETE);

        if (editingItemId === String(item.id)) {
          resetForm();
        }

        await fetchListItems();

        // Update parent with new data
        const universitySettingsId = getUniversitySettingsId(selectedSection?.componentId, listItems);
        if (universitySettingsId && fetchMethod) {
          try {
            const response = await fetchMethod(universitySettingsId);
            const updatedItems = extractItemsFromResponse(response);
            onSave({
              ...selectedSection,
              componentId: String(universitySettingsId),
              data: updatedItems,
            });
          } catch (error) {
            console.error("Failed to fetch updated data after deletion:", error);
          }
        }
      } catch (error) {
        console.error("Failed to delete:", error);
        showToast.error(DEFAULT_MESSAGES.ERROR_DELETE);
      }
    },
    [deleteMethod, editingItemId, resetForm, fetchListItems, selectedSection, listItems, fetchMethod, onSave]
  );

  const handleNewItem = useCallback(() => {
    resetForm();
  }, [resetForm]);

  return {
    // State
    listItems,
    editingItemId,
    componentFields,
    componentFieldErrors,
    componentFieldMessages,
    componentFieldsConfig,
    // Actions
    initializeData,
    initializeFormDefaults,
    handleFieldsUpdate,
    handleSave,
    handleEdit,
    handleDelete,
    handleNewItem,
  };
};

